import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useAnalytics } from 'use-analytics';
import { useMediaQuery } from 'react-responsive';
import Filter from '../../../../Filters/Filter';
import TwSlider from '../../../../TwSlider';
import hotelSearchInput from '../../../../../helpers/selectors/hotelSearchInput';
import { setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { hotelSearch } from '../../../../../store/slices/hotel';
import { convertDistance, DistanceUnit } from '../../../../../constants';

const MIN_RADIUS = 3;
const MAX_RADIUS = 200;

const HotelRadiusFilter = ({ tripId, variant, filterBgColor }) => {
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const isTripSearch = !tripId;
  const { searchInputs, searchObj } = useSelector(hotelSearchInput(tripId, isTripSearch));
  const [sliderValue, setSliderValue] = useState(searchObj.searchRadiuses?.[0]);
  const values = isTripSearch ? searchInputs.searchRadiuses : searchObj.searchRadiuses;
  const { travelAgency } = useSelector((state) => state.user);
  const defaultRadiuses = travelAgency?.hotelSearchRadiuses;
  const baseRadius = sliderValue ?? defaultRadiuses?.[0] ?? 0;

  const distanceUnit =
    travelAgency?.settings?.hotelSearchRadiusUnit === 'KM'
      ? DistanceUnit.KILOMETERS
      : DistanceUnit.MILES;

  const displayUnit = distanceUnit === DistanceUnit.KILOMETERS ? 'km' : 'miles';
  const displayRadius = convertDistance(baseRadius, distanceUnit);

  useEffect(() => {
    setSliderValue(values?.[0] || defaultRadiuses?.[0]);
  }, [values, defaultRadiuses]);

  const makePayload = useCallback(
    (radius) => ({
      tripId,
      searchObj: {
        searchRadiuses: [radius, ...(defaultRadiuses?.slice(1) || [])],
      },
    }),
    [tripId, defaultRadiuses],
  );

  const handleClear = useCallback(() => {
    const payload = makePayload(defaultRadiuses?.[0]);
    if (isTripSearch) {
      dispatch(setHotelTripSearchInputs(payload.searchObj));
    } else {
      dispatch(hotelSearch(payload));
    }
    track('HotelRadiusFilter.handleClear');
  }, [dispatch, isTripSearch, makePayload, track, defaultRadiuses]);

  const handleChange = useCallback((event, newValue) => {
    setSliderValue(newValue);
  }, []);

  const handleChangeCommitted = useCallback(
    (event, newValue) => {
      const payload = makePayload(newValue);
      if (isTripSearch) {
        dispatch(setHotelTripSearchInputs(payload.searchObj));
      } else {
        dispatch(hotelSearch(payload));
      }
      track('HotelRadiusFilter.handleChange', { payload });
    },
    [dispatch, isTripSearch, makePayload, track],
  );

  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });

  const label = useMemo(
    () => (sliderValue ? `Radius: ${Math.round(displayRadius)} ${displayUnit}` : 'Radius'),
    [sliderValue, displayRadius, displayUnit],
  );

  return (
    <Filter
      label={label}
      tooltipPrependText={label}
      menuWidthClass="w-max"
      isFilterEmpty={sliderValue === defaultRadiuses?.[0]}
      handleClear={handleClear}
      disableCloseOnOutsideClick
      variant={variant}
      bgClassName={!isTripSearch ? '' : filterBgColor}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
    >
      <div className="px-4 pb-1 pt-4">
        <div className="grid grid-cols-3 items-center mb-2">
          <div className="text-sm text-black font-bold">Radius:</div>
          <div className="col-span-2 text-right ml-4 text-sm text-black font-bold">
            {`${Math.round(displayRadius)} ${displayUnit}`}
          </div>
          <div className="text-xs text-gray-700 col-span-3">
            Search results will include hotels <br /> within this distance from your selected
            location
          </div>
        </div>
        <TwSlider
          value={sliderValue}
          min={MIN_RADIUS}
          max={MAX_RADIUS}
          step={1}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
        />
      </div>
    </Filter>
  );
};

HotelRadiusFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

HotelRadiusFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterBgColor: undefined,
};

export default HotelRadiusFilter;
